import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm";
import StripePayment from "./StripePayment";
import { IoIosCloseCircleOutline } from "react-icons/io";

const StripeSubscriptionPayment = ({
  id,
  amount,
  currency,
  isOpen,
  onClose,
  onPaymentSuccess,
  planType, // "Affiliate Partner" or "Affiliate Partner Soft Launch"
  referralCode, // <-- Add this prop
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCollectCard, setShowCollectCard] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  // Convert amount to minor units (cents for USD)
  const toMinorUnits = (amt) => {
    if (amt == null) return amt;
    const n = typeof amt === "string" ? parseFloat(amt) : Number(amt);
    if (!isFinite(n)) return amt;
    return Math.round(n * 100);
  };

  useEffect(() => {
    if (!isOpen) return;

    console.log("[StripeSubscriptionPayment] Props received:", {
      id,
      amount,
      amountMinorUnits: toMinorUnits(amount),
      currency,
      isOpen,
      planType,
      referralCode,
    });
  }, [id, amount, currency, isOpen, planType, referralCode]);

  // useEffect(() => {
  //   // Load Stripe (test key for now)
  //   const promise = loadStripe(
  //     "pk_test_51TccjfRFarMdNoXReDAN0f7YEzC3YHqAlDkM1lJge96gHjFuUEdZ7QKWOrlLAY6SFy7WScXrmteYW38tgnPEoRKa00F6vkIUCI"
  //   );
  //   setStripePromise(promise);
  // }, []);

  useEffect(() => {
    // Load Stripe publishable key from environment (live key expected in production)
    const pk =
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
      // Fallback to the same live key used elsewhere in the app
      "pk_live_51RjwCNH4SEOW1T3d8NbnBAfQFvuPQjXVIlqDEuAiKXkAnjk1opYg86mCJdU6aikNOisXhwyADFh9VRq6Ue9r88gi00HTySzJLr";
    if (!pk) {
      // console.error(
      //   "Missing REACT_APP_STRIPE_PUBLISHABLE_KEY. Stripe.js cannot initialize."
      // );
    }
    const promise = loadStripe(pk || "");
    setStripePromise(promise);
  }, []);

  useEffect(() => {
    const createSubscriptionPayment = async () => {
      console.log("=== StripeSubscriptionPayment useEffect triggered ===");
      console.log("isOpen:", isOpen);
      console.log("planType:", planType);
      console.log("incoming amount/currency:", {
        amount,
        amountMinorUnits: toMinorUnits(amount),
        currency,
      });

      // Get user email and name from localStorage
      const email = localStorage.getItem("email") || "";
      setUserEmail(email);
      let userName =
        localStorage.getItem("name") ||
        localStorage.getItem("username") ||
        localStorage.getItem("Username") ||
        "";

      // If username is still empty, try to get it from email (use part before @)
      if (!userName && email) {
        userName = email.split("@")[0];
      }

      console.log(
        "Retrieved from localStorage - email:",
        email,
        "username:",
        userName
      );
      console.log("All localStorage keys:", Object.keys(localStorage));

      if (!email) {
        setError(
          "User email is required for subscription payment. Please log in again."
        );
        setLoading(false);
        return;
      }

      if (!userName) {
        setError(
          "User name is required for subscription payment. Please log in again."
        );
        setLoading(false);
        return;
      }

      // Determine which API endpoint and price IDs to use based on planType
      const isInternalPartner = planType === "Internal Partner";
      const apiEndpoint = isInternalPartner
        ? "https://xianhefairy.com/LotusFairy_API/Payment/SubscriptionPaymentInternal"
        : "https://xianhefairy.com/LotusFairy_API/Payment/SubscriptionPaymentSoft";

      //test
      // const apiEndpoint = isInternalPartner
      //   ? "https://xianhefairy.com/LotusFairy_API/Payment/SubscriptionPaymentInternal"
      //   : "https://xianhefairy.com/LotusFairy_API/Payment/SubscriptionPaymentSoft";
      const oneTimePriceId = isInternalPartner
        ? "price_1TtmWzH4SEOW1T3dU8a76uaj" // $399 one-time for Internal Partner
        : "price_1TgFwyH4SEOW1T3dti46Cat0"; // $99 one-time for Affiliate Partner

      //test
      // const oneTimePriceId = isInternalPartner
      //   ? "price_1Tu2kyRFarMdNoXRPN5VLnqF" // $399 one-time for Internal Partner
      //   : "price_1Tg22PRFarMdNoXRqkDr7HpX"; // $99 one-time for Affiliate Partner
      // Build payload for subscription API. Do NOT send a PaymentMethodId.
      // Let backend create an incomplete PaymentIntent and return a
      // clientSecret for the PaymentElement to confirm on the client.
      const subscriptionBody = {
        Email: email,
        CustomerName: userName,
        OneTimePriceId: oneTimePriceId,
        // MonthlyPriceId: "price_1Sa9j3H4SEOW1T3dYDz0GftG",
      };

      try {
        setLoading(true);
        setError(null);

        console.log(
          `Creating ${
            isInternalPartner ? "Internal Partner" : "Affiliate Partner"
          } subscription payment with:`,
          {
            Email: email,
            CustomerName: userName,
            OneTimePriceId: subscriptionBody.OneTimePriceId,
            MonthlyPriceId: subscriptionBody.MonthlyPriceId,
            Endpoint: apiEndpoint,
          }
        );

        const response = await fetch(apiEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(subscriptionBody),
        });

        // console.log(
        //   "API Response Status:",
        //   response.status,
        //   response.statusText
        // );

        if (!response.ok) {
          let txt = "";
          try {
            txt = await response.text();
            // console.error("API Error Response Body:", txt);
          } catch (e) {
            txt = `<unable to read response body: ${e.message}>`;
          }
          throw new Error(`Failed to create subscription payment: ${txt}`);
        }

        const data = await response.json();
        console.log("Subscription response:", data);

        // Prefer the oneTime payment clientSecret, then fall back to
        // subscription.clientSecret.
        let clientSecretValue = null;
        let oneTimePayment = null;

        if (data.oneTime && data.oneTime.clientSecret) {
          clientSecretValue = data.oneTime.clientSecret;
          oneTimePayment = data.oneTime;
          console.log("✅ Using oneTime payment clientSecret");
        } else if (data.subscription && data.subscription.clientSecret) {
          clientSecretValue = data.subscription.clientSecret;
          console.log("✅ Using subscription clientSecret");
        }

        const subscriptionId = data.subscription?.id || null;

        if (clientSecretValue) {
          setClientSecret(clientSecretValue);
          setSubscriptionData({
            id: subscriptionId,
            status: data.subscription?.status || "trialing",
            oneTimePayment: oneTimePayment,
            stripeCustomer:
              data.subscription?.customer || data.customer || "",
          });

          if (data && data.error) {
            // console.warn("⚠️ Backend returned error message:", data.error);
          }
        } else {
          // console.error("❌ No client secret found in response");
          if (data && data.error) {
            setError(data.error);
            return;
          }
          throw new Error(
            "No client secret found in API response. Cannot process payment."
          );
        }
      } catch (error) {
        // console.error("Error creating subscription payment:", error);
        setError(`Error: ${error.message}. Check browser console for details.`);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      createSubscriptionPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, amount, currency, planType]);

  // helper used in render to decide one-time amount for fallback card collection
  const isInternalPartner = planType === "Internal Partner";

  const appearance = {
    theme: "stripe",
    variables: {
      colorPrimary: "#deaf77",
      colorBackground: "#ffffff",
      colorText: "#131020",
      colorTextSecondary: "#ffffff",
      colorTextPlaceholder: "#9ca3af",
      colorDanger: "#ef4444",
      fontFamily: "DMSans, system-ui, sans-serif",
      borderRadius: "6px",
    },
    rules: {
      ".Label": {
        color: "#ffffff",
        fontWeight: "500",
      },
      ".AccordionItem": {
        color: "#ffffff",
        backgroundColor: "#1c1733",
      },
      ".Tab": {
        color: "#ffffff",
      },
      ".TabLabel": {
        color: "#ffffff",
      },
      ".Input": {
        color: "#131020",
      },
    },
  };

  const handleSubscriptionPaymentSuccess = (paymentIntent) => {
    // When the client confirms payment successfully, persist the
    // subscription to our backend so it's tied to the customer.
    (async () => {
      let saveStatus = "skipped"; // 'success' | 'failed' | 'skipped'
      let saveError = null;
      try {
        // Determine success: either the one-time payment intent succeeded
        // or the subscription is active. Accept 'succeeded' or 'processing'.
        const piStatus = paymentIntent && paymentIntent.status;
        const subStatus = subscriptionData && subscriptionData.status;

        const isPaymentSucceeded =
          (piStatus &&
            (piStatus === "succeeded" || piStatus === "processing")) ||
          (subStatus && subStatus === "active");

        if (!isPaymentSucceeded) {
          // console.warn(
          //   "Payment not in succeeded/active state, skipping save.",
          //   { piStatus, subStatus }
          // );
        } else {
          try {
            const localUserId = localStorage.getItem("userID") || "";
            const saveBody = {
              CUST_ID: Number(localUserId) || 0,
              // Prefer the PaymentIntent id (pi_...) when available, otherwise
              // fall back to the returned Stripe customer id or subscription id.
              STRIPE_ID:
                (paymentIntent && paymentIntent.id) ||
                (subscriptionData &&
                  (subscriptionData.stripeCustomer || subscriptionData.id)) ||
                "",
              SUB_ID: (subscriptionData && subscriptionData.id) || "1 time payment",
              SUB_DESC: planType || "Subscription",
              AMOUNT: amount || 0,
              REF_CODE: referralCode || "", // <-- Referral code being sent
              CREATED_BY: userEmail || "test",
              UPDATED_BY: userEmail || "test",
            };

            console.log(
              "🟢 [affiliate PARTNER] Saving subscription to backend (post-confirmation):",
              saveBody
            );
            console.log(
              "🟢 [affiliate PARTNER] Payment Intent Status:",
              piStatus
            );
            console.log(
              "🟢 [affiliate PARTNER] Subscription Status:",
              subStatus
            );
            console.log("🟢 [affiliate PARTNER] Plan Type:", planType);

            const saveResp = await fetch(
              "https://xianhefairy.com/LotusFairy_API/Payment/SaveStripeSubscription",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(saveBody),
              }
            );

            //test
            // const saveResp = await fetch(
            //   "https://xianhefairy.com/LotusFairy_API/Payment/SaveStripeSubscription",
            //   {
            //     method: "POST",
            //     headers: { "Content-Type": "application/json" },
            //     body: JSON.stringify(saveBody),
            //   }
            // );

            // console.log(
            //   "🟢 [affiliate PARTNER] SaveStripeSubscription API Response Status:",
            //   saveResp.status,
            //   saveResp.statusText
            // );

            const saveText = await saveResp.text();
            let parsed = null;
            try {
              parsed = saveText ? JSON.parse(saveText) : null;
              console.log(
                "SaveStripeSubscription response:",
                parsed ?? saveText
              );
            } catch (e) {
              console.log("SaveStripeSubscription response (text):", saveText);
            }

            if (!saveResp.ok) {
              console.error(
                "🔴 [affiliate PARTNER] SaveStripeSubscription FAILED! Status:",
                saveResp.status
              );
              saveStatus = "failed";
              saveError = `SaveStripeSubscription returned ${saveResp.status} ${saveResp.statusText}`;
            } else if (parsed && parsed.success === false) {
              console.error(
                "🔴 [affiliate PARTNER] SaveStripeSubscription returned success:false:",
                parsed.message
              );
              saveStatus = "failed";
              saveError = parsed.message || "SaveStripeSubscription returned success:false";
            } else {
              console.log(
                "✅ [affiliate PARTNER] SaveStripeSubscription SUCCESS!"
              );
              saveStatus = "success";
            }
          } catch (e) {
            // console.error(
            //   "🔴 [AFFILIATE PARTNER] EXCEPTION saving subscription to backend:",
            //   e
            // );
            // console.error(
            //   "🔴 [AFFILIATE PARTNER] Error details:",
            //   e.message,
            //   e.stack
            // );
            saveStatus = "failed";
            saveError = e && e.message ? e.message : String(e);
          }
        }
      } finally {
        // Always forward the success event to parent so UI updates as before
        try {
          onPaymentSuccess({
            paymentIntent,
            subscription: subscriptionData,
            saveStatus,
            saveError,
          });
        } catch (e) {
          // console.error("onPaymentSuccess handler error:", e);
        }
      }
    })();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.2)",
        zIndex: 2147483646,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        overflow: "visible",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          backgroundColor: "#131020",
          borderRadius: 12,
          borderColor: "#C9A84B66",
          borderWidth: 2,
          borderStyle: "solid",
          padding: 16,
          boxShadow: "0px 4px 16px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          width: "min(98vw, 640px)",
          maxWidth: "98vw",
          maxHeight: "calc(100vh - 32px)",
          overflow: "visible",
          zIndex: 2147483647,
        }}
      >
        <div
          style={{ width: "100%", display: "flex", justifyContent: "flex-end", zIndex: 2147483647 }}
        >
          <button
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "24px",
              color: "#deaf77",
            }}
            onClick={onClose}
          >
            <IoIosCloseCircleOutline />
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            paddingRight: 0,
            display: "block",
            width: "100%",
          }}
        >
          {loading ? (
            <p>Loading subscription payment...</p>
          ) : showCollectCard ? (
            <div style={{ padding: 6 }}>
              <p style={{ marginTop: 0 }}>We need a payment method to continue.</p>
              <p style={{ marginTop: 6, marginBottom: 6 }}>
                Please add a card below — if Stripe resources are blocked by an
                ad-blocker you may need to disable it for this site.
              </p>
              <div style={{ marginTop: 8 }}>
                <StripePayment
                  id={planType}
                  amount={isInternalPartner ? 399 : 99}
                  currency={currency}
                  isOpen={true}
                  onClose={() => setShowCollectCard(false)}
                  onPaymentSuccess={(paymentIntent) => {
                    try {
                      handleSubscriptionPaymentSuccess(paymentIntent);
                    } catch (e) {
                      // console.error("Error forwarding payment success:", e);
                    }
                  }}
                />
              </div>
            </div>
          ) : clientSecret && stripePromise ? (
            // ✅ FIXED: Show payment form even if there's an error (like Internal Partner does)
            <>
              {error && (
                <div
                  style={{
                    color: "#deaf77",
                    padding: "12px",
                    backgroundColor: "#fff3cd",
                    borderRadius: "4px",
                    marginBottom: "16px",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "14px" }}>{error}</p>
                </div>
              )}
              <Elements
                stripe={stripePromise}
                options={{ 
                          clientSecret, 
                          appearance,
                          layout: {
                            type: 'accordion',
                            defaultCollapsed: true,
                            radios: false,
                            spacedAccordionItems: false
                          }
                        }}
              >
                <CheckoutForm
                  onClose={onClose}
                  onPaymentSuccess={handleSubscriptionPaymentSuccess}
                  planType={planType}
                />
              </Elements>
            </>
          ) : error ? (
            <div
              style={{
                color: "red",
                padding: "20px",
              }}
            >
              <h3>Error</h3>
              <p>{error}</p>
              <button
                onClick={onClose}
                style={{
                  marginTop: "10px",
                  padding: "8px 16px",
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          ) : (
            <p>
              Unable to initialize subscription payment. Please try again later.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StripeSubscriptionPayment;